import { createStore, RouterState } from '@oneappexchange/lwc-wired';
import { State } from 'zustand';
import { BUCKETS, IS_DEVELOPMENT } from '~/modules/config';
import { FeatureFlags } from '~/modules/feature_flags';
import { getPreferredLanguage } from '~/modules/helpers/languageHelper';
import { EXPLORE_BASE_ROUTE } from '~/modules/routes';
import { ExploreResultListing, getBucketSections } from '~/modules/services/exploreService';
import { i18nStore } from '~/modules/stores/i18nStore';
import { FeatureFlagStoreStatus, featureFlagsStore, selectStatus } from '~/modules/stores/feature_flags_store';
import { routerStore } from '~/modules/stores/routerStore';
import { handleException } from '~/modules/utilities/error_utilities';
import { JAPANESE_LANGUAGE_CODE } from '~/modules/utilities/language_utils';
import { isSlackListingsEnabled } from '~/modules/utilities/slackPilot';

export interface BucketSection {
    name: string;
    label: string;
    url: string;
    listings: ExploreResultListing[];
}

export const RESULT_STATUS = {
    LOADING: 'LOADING',
    ERROR: 'ERROR',
    EMPTY: 'EMPTY',
    DONE: 'DONE',
};

export type ResultStatus = (typeof RESULT_STATUS)[keyof typeof RESULT_STATUS];

type Buckets = (typeof BUCKETS)[keyof typeof BUCKETS];

export interface BucketResult {
    status: ResultStatus;
    sections: BucketSection[];
}

export type SectionLabels =
    | I18nMessages['oaf']['category']
    | I18nMessages['search']['filters']['product']
    | I18nMessages['search']['filters']['solutionType']
    | I18nMessages['search']['filters']['solutionType_section']
    | I18nMessages['search']['filters']['impact_section']
    | I18nMessages['oaf']['supportedIndustries'];

export interface ExploreState extends State {
    currentBucket: Buckets;
    bucketSections: Map<Buckets, BucketResult>;
}

const initialState: ExploreState = {
    currentBucket: '',
    bucketSections: new Map<Buckets, BucketResult>(),
};

export const getLoadingBucketResult = (): BucketResult => {
    return {
        status: RESULT_STATUS.LOADING,
        sections: [],
    };
};

export type CurrentBucketResults = { bucketResult: BucketResult | undefined; currentBucket: string };
export const selectBucketResults = (state: ExploreState): CurrentBucketResults => {
    return {
        bucketResult: state.bucketSections.get(state.currentBucket),
        currentBucket: state.currentBucket,
    };
};

export const selectCurrentBucket = (state: ExploreState): string => {
    return state.currentBucket;
};

export const getSectionLabel = (bucket: string) => {
    switch (bucket) {
        case BUCKETS.EXPLORE_BUSINESS_NEEDS:
            return i18nStore.getState().i18nStaticCache.oaf.category;
        case BUCKETS.EXPLORE_PRODUCTS:
            return i18nStore.getState().i18nStaticCache.search.filters.product;
        case BUCKETS.EXPLORE_INDUSTRIES:
            return i18nStore.getState().i18nStaticCache.oaf.supportedIndustries;
        case BUCKETS.EXPLORE_IMPACT:
            return i18nStore.getState().i18nStaticCache.search.filters.impact_section;
        case BUCKETS.EXPLORE_LABS:
            if (getPreferredLanguage() === JAPANESE_LANGUAGE_CODE) {
                return i18nStore.getState().i18nStaticCache.search.filters.solutionType;
            }
            return i18nStore.getState().i18nStaticCache.search.filters.solutionType_section;
        default:
            return undefined;
    }
};

export const exploreStore = createStore(
    initialState,

    (set: SetState<ExploreState>, get: GetState<ExploreState>) => ({
        setError(bucket: Buckets, error: Error) {
            set(function setError(draft: ExploreState) {
                draft.bucketSections.set(bucket, {
                    status: RESULT_STATUS.ERROR,
                    sections: [],
                });
                return draft;
            });
            handleException(error, 'exploreStore.setError', { debugData: { bucket } });
        },
        setLoading(bucket: Buckets) {
            set(function setLoading(draft: ExploreState) {
                draft.bucketSections.set(bucket, getLoadingBucketResult());
                return draft;
            });
        },
        setCurrentBucket(bucket: Buckets) {
            set(function setBucket(draft: ExploreState) {
                draft.currentBucket = bucket;
                return draft;
            });
        },
        async fetchBucketSections(bucket: Buckets, forceFetch = false, limit?: number) {
            const { currentBucket, bucketSections } = get();

            const currentSection: BucketResult | undefined = bucketSections.get(bucket);

            if (!forceFetch) {
                if (currentBucket === bucket && currentSection?.status === RESULT_STATUS.DONE) {
                    return;
                }

                if (currentSection) {
                    set(function updateCategoryResults(draft: ExploreState) {
                        draft.currentBucket = bucket;
                        return draft;
                    });
                    return;
                }
            }

            this.setCurrentBucket(bucket);
            this.setLoading(bucket);

            try {
                const labels = getSectionLabel(bucket);
                if (labels) {
                    const sections = await getBucketSections(bucket, labels, limit);
                    const bucketStatus: ResultStatus = sections.length === 0 ? RESULT_STATUS.EMPTY : RESULT_STATUS.DONE;
                    const bucketResult: BucketResult = {
                        status: bucketStatus,
                        sections: sections,
                    };
                    set(function updateCategoryResults(draft: ExploreState) {
                        draft.bucketSections.set(bucket, bucketResult);
                    });
                } else {
                    set(function updateCategoryResults(draft: ExploreState) {
                        draft.bucketSections.set(bucket, {
                            status: RESULT_STATUS.EMPTY,
                            sections: [],
                        });
                    });
                }
            } catch (error) {
                this.setError(bucket, error);
            }
        },
        reset() {
            set(function resetStore() {
                return initialState;
            });
        },
    }),
    'exploreStore',
    IS_DEVELOPMENT,
);

const getBrowseTeaserCount = (): number => {
    return FeatureFlags.FF_EXPLORE_PAGE_ENHANCEMENTS && isSlackListingsEnabled() ? 3 : 2;
};

routerStore.subscribe(
    ({ data, url, params }) => {
        const isExploreRoute = url.indexOf(EXPLORE_BASE_ROUTE) !== -1;

        if (!isExploreRoute) {
            exploreStore.reset();
            return;
        }

        const bucket = (data?.bucket ?? params?.bucket) as Buckets | undefined;
        if (!bucket) {
            return;
        }

        exploreStore.fetchBucketSections(bucket, false, getBrowseTeaserCount());
    },
    (state: RouterState) => ({ data: state.data, url: state.url, params: state.params }),
);

// When feature flags finish loading after the initial router subscription has already cached
// explore results, force-refetch the current bucket so the correct teaser count is used.
// pendingFlagsRefetchBucket tracks the specific bucket that was in-flight when flags loaded,
// so a user navigating to a different bucket during flag hydration cannot consume the pending
// refetch for the wrong bucket or leave the original bucket cached with a stale count.
// Using selectStatus (raw enum) rather than selectFeatureFlagsLoaded (boolean LOADED||ERROR)
// means a subsequent Unleash recovery from ERROR -> LOADED is also detected and applied.
let previousFlagsStatus = selectStatus(featureFlagsStore.getState());
let pendingFlagsRefetchBucket: Buckets | undefined;

featureFlagsStore.subscribe((flagsStatus) => {
    if (previousFlagsStatus !== FeatureFlagStoreStatus.LOADED && flagsStatus === FeatureFlagStoreStatus.LOADED) {
        const { currentBucket, bucketSections } = exploreStore.getState();
        const currentSection = bucketSections.get(currentBucket);

        if (currentBucket && currentSection?.status === RESULT_STATUS.DONE) {
            exploreStore.fetchBucketSections(currentBucket, true, getBrowseTeaserCount());
        } else if (currentBucket && currentSection?.status === RESULT_STATUS.LOADING) {
            pendingFlagsRefetchBucket = currentBucket;
        }
    }
    previousFlagsStatus = flagsStatus;
}, selectStatus);

exploreStore.subscribe(
    ({ bucket, bucketResult }) => {
        if (!bucket) {
            return;
        }

        if (bucketResult?.status === RESULT_STATUS.DONE) {
            const activeBucket = exploreStore.getState().currentBucket;
            pendingFlagsRefetchBucket = undefined;
            exploreStore.fetchBucketSections(bucket, true, getBrowseTeaserCount());
            if (activeBucket !== bucket) {
                exploreStore.setCurrentBucket(activeBucket);
            }
        } else if (bucketResult?.status === RESULT_STATUS.ERROR || bucketResult?.status === RESULT_STATUS.EMPTY) {
            pendingFlagsRefetchBucket = undefined;
        }
    },
    (state: ExploreState) => ({
        bucket: pendingFlagsRefetchBucket,
        bucketResult: pendingFlagsRefetchBucket ? state.bucketSections.get(pendingFlagsRefetchBucket) : undefined,
    }),
);
